import type { PublicTrustSnapshot, TrustSealState } from '../types';
import { trustHeroSealLabel, trustSealState, safeHttpUrl } from '../trustSeal';
import { CeHexGlyph } from './CeHexGlyph';

/** Dark text on every fill, as badge_svg.py draws it: white measured 2.54:1 on
 *  green and 3.76:1 on red; #1a1a1a clears 6.86:1, 8.1:1 and 4.62:1
 *  (CE-REVIEW-TRUST-012 F1). */
const SEAL_STATUS_SURFACE: Record<TrustSealState, { panel: string; dot: string }> = {
  active: { panel: 'bg-emerald-500 text-[#1a1a1a]', dot: 'fill-emerald-500' },
  recent: { panel: 'bg-amber-500 text-[#1a1a1a]', dot: 'fill-amber-500' },
  idle: { panel: 'bg-red-500 text-[#1a1a1a]', dot: 'fill-red-500' },
};

/**
 * Trust-page enforcement seal: flexbox-tight, not the embed SVG.
 * Embed badge SVG uses fixed panel widths for <img> widgets; on the trust
 * page that leaves dead space when scaled. This component hugs its label.
 */
export function TrustHeroSeal({
  snapshot,
  href,
  linkLabel,
}: {
  snapshot: PublicTrustSnapshot;
  href?: string;
  linkLabel?: string;
}) {
  const state = trustSealState(snapshot);
  const styles = SEAL_STATUS_SURFACE[state];
  const label = trustHeroSealLabel(snapshot);

  const seal = (
    <div className="trust-hero-seal inline-flex items-stretch overflow-hidden rounded-[5px] text-[10px] font-medium leading-none shadow-[0_4px_14px_rgba(0,0,0,0.35)] md:text-[11px]">
      <span className="inline-flex items-center gap-2 border-r border-[#e6a98c]/50 bg-[#1a1a1a] py-1.5 pl-2.5 pr-3 md:py-2">
        <CeHexGlyph dotClassName={styles.dot} size={24} />
        <span className="whitespace-nowrap font-display text-[10px] font-semibold text-[#f5f5f5] md:text-[11px]">
          ComplyEdge
        </span>
      </span>
      <span
        className={`inline-flex items-center whitespace-nowrap px-3 py-1.5 font-sans text-[10px] font-medium md:py-2 md:text-[11px] ${styles.panel}`}
      >
        {label}
      </span>
    </div>
  );

  const safeHref = safeHttpUrl(href);
  if (safeHref) {
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block rounded-[5px] transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/60 focus-visible:outline-offset-2"
        aria-label={linkLabel ?? 'ComplyEdge enforcement seal'}
      >
        {seal}
      </a>
    );
  }

  return seal;
}
